import { useEffect, type ReactNode } from "react";
import { Link, Navigate, Outlet } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useTenant } from "@/lib/tenant";
import { usePortalAuth } from "@/lib/portalAuth";
import { BRANDS, setBrandSideEffects, type ProductBrand } from "@/lib/branding";
import Login from "@/screens/auth/Login";
import TwoFactorChallenge from "@/screens/auth/TwoFactorChallenge";

const Loading = () => (
  <div style={{ display: "grid", placeItems: "center", height: "100vh" }}><div className="kicker">Loading…</div></div>
);

export function RequireAuth({ children }: { children: ReactNode }) {
  const { user, twoFactorRequired } = useAuth();
  if (twoFactorRequired) return <Navigate to="/auth/2fa" replace />;
  if (!user) return <Navigate to="/auth/login" replace />;
  return <>{children}</>;
}

export function RequireSuperAdmin({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  if (!user?.is_super_admin) return <Navigate to="/" replace />;
  return <>{children}</>;
}

/** Owner/admin (or super admin) only, mirroring the menu's minRole "admin". */
export function RequireTenantAdmin({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const { current } = useTenant();
  if (user?.is_super_admin) return <>{children}</>;
  const role = current?.pivot?.role ?? "";
  if (role !== "owner" && role !== "admin") return <Navigate to="/" replace />;
  return <>{children}</>;
}

export function RequirePermission({ permission, title }: { permission: string; title: string }) {
  const { user, permissions } = useAuth();
  if (user?.is_super_admin || permissions.includes(permission)) return <Outlet />;
  return (
    <div className="content-inner">
      <div style={{ maxWidth: 420, margin: "40px auto", textAlign: "center" }}>
        <h1 style={{ fontSize: 20, marginBottom: 8 }}>{title}</h1>
        <p className="muted" style={{ marginBottom: 20 }}>
          Your account doesn’t have permission for this area. Ask an administrator for access.
        </p>
        <Link to="/" className="btn">Back to dashboard</Link>
      </div>
    </div>
  );
}

export function RequirePortalAuth({ children }: { children: ReactNode }) {
  const { loading, user } = usePortalAuth();
  if (loading) return <Loading />;
  if (!user) return <Navigate to="/auth/login" replace />;
  return <>{children}</>;
}

export function PortalGuestRedirect({ children }: { children: ReactNode }) {
  const { user } = usePortalAuth();
  if (user) return <Navigate to="/portal" replace />;
  return <>{children}</>;
}

export function LoginRoute() {
  const { user, twoFactorRequired } = useAuth();
  return user && !twoFactorRequired ? <Navigate to="/" replace /> : <Login />;
}

export function TwoFactorRoute() {
  const { user, twoFactorRequired } = useAuth();
  return !twoFactorRequired ? <Navigate to={user ? "/" : "/auth/login"} replace /> : <TwoFactorChallenge />;
}

/** Brand for pages outside the staff shell (auth, share, portal, get-app). */
export function Branded({ brand }: { brand: ProductBrand }) {
  useEffect(() => { setBrandSideEffects(BRANDS[brand]); }, [brand]);
  return <Outlet />;
}
